<template>
  <Dialog
    title="Create Manual Order"
    :show-cancel-button="true"
    :show-save-button="true"
    :show-footer="true"
    :show-close-button="true"
    save-button-text="Create Order"
    :save-button-props="{ form: 'create-manual-order-form' }"
    :save-button-disabled="loading || uploadingFiles"
    @closeEvent="closeManualOrderModal"
    @cancelEvent="closeManualOrderModal"
    :md="true"
  >
    <template #modal-content>
      <Form
        id="create-manual-order-form"
        class="pt-4"
        @submit="createOrder"
        v-slot="{ errors: formErrors, submitCount, values, controlledValues }"
      >
        <div class="flex flex-col w-full gap-2">
          <!-- General Information Section -->
          <Accordion :default-open="true">
            <template #header>
              <div
                :class="{
                  'text-red-500': submitCount > 0 && hasGeneralInfoErrors(errorMessages),
                }"
              >
                General Information
                <span v-if="orderTotalPrice.price">
                  {{ orderTotalPrice.price }}{{ orderTotalPrice.currency_code }}
                </span>
                <br />
                <span v-if="submitCount > 0 && hasGeneralInfoErrors(errorMessages)" class="text-sm">
                  Some fields couldn't be validated
                </span>
              </div>
            </template>
            <template #content>
              <div class="grid grid-cols-2 gap-4">
                <!-- Merchant Select -->
                <div>
                  <label for="merchant-select" class="block mb-1 text-sm font-medium text-gray-700"
                    >Select Merchant
                    <span class="text-red-500">*</span>
                  </label>
                  <Select
                    append-to-body
                    v-model="manualOrderData.merchant"
                    :options="companies"
                    label="company_name"
                    placeholder="Select Merchant"
                    :clearable="false"
                    @option:selected="handleMerchantUpdate"
                    :class="{ 'border-red-500': errorMessages['merchant'] }"
                  />
                  <span v-if="errorMessages['merchant']" class="text-xs text-red-500">{{
                    errorMessages['merchant']
                  }}</span>
                </div>

                <!-- Customer Select -->
                <div>
                  <label for="user-select" class="block mb-1 text-sm font-medium text-gray-700"
                    >Select Customer <span class="text-red-500">*</span>
                  </label>
                  <Select
                    append-to-body
                    v-model="manualOrderData.customer"
                    :options="customers"
                    label="name"
                    placeholder="Select Customer"
                    :clearable="false"
                    @option:selected="handleCustomerUpdate"
                    :class="{ 'border-red-500': errorMessages['customer'] }"
                  >
                  <template #option="{ id, name }">
                    <div class="flex items-center">
                      <!-- Default 'Create Customer' option -->
                      <template v-if="id == 0">
                        <PlusIcon class="w-5 h-5 mr-1 text-green-600" />
                        <span>Create Customer</span>
                      </template>

                      <!-- All normal customer options -->
                      <template v-else>
                        <span>{{ name }}</span>
                      </template>
                    </div>
                  </template>
                </Select>
                  <span v-if="errorMessages['customer']" class="text-xs text-red-500">{{
                    errorMessages['customer']
                  }}</span>
                </div>

                <!-- Order Reference -->
                <div>
                  <label for="order-reference" class="block mb-1 text-sm font-medium text-gray-700"
                    >Order Reference
                  </label>
                  <input
                    v-model="manualOrderData.customer_reference_no"
                    type="text"
                    placeholder="Enter order reference"
                    class="input"
                  />
                </div>
                <!-- PO Number -->
                <div>
                  <label for="po-number" class="block mb-1 text-sm font-medium text-gray-700"
                    >PO Number</label
                  >
                  <input
                    v-model="manualOrderData.po_number"
                    id="po-number"
                    type="text"
                    placeholder="Enter PO number"
                    class="input"
                  />
                </div>
                <!-- PO Document -->
                <div>
                  <label for="po-document" class="block mb-1 text-sm font-medium text-gray-700"
                    >PO Document (Optional)</label
                  >
                  <!-- <input
                    type="file"
                    accept=".pdf"
                    @change="(e) => handleFileUpload(e, 'po_document')"
                    @focus="focusedField = { fieldName: 'po_document' }"
                    @blur="focusedField = null"
                    class="block w-full mt-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  /> -->
                  <CommonFileUploader
                    accept=".pdf"
                    fieldName="po_document"
                    outerClass="w-full"
                    @file-change="(data)=>handleFileUpload({target:{files:data.files, type:'file'}}, data.fieldName, data.index, '.pdf')"
                    @focus="focusedField = $event"
                    @blur="focusedField = null"
                  />
                  <div
                    v-if="manualOrderData.po_document"
                    class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden mt-2"
                  >
                    <button
                      @click="manualOrderData.po_document = null"
                      class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                    >
                      <TrashIcon class="w-4 h-4" />
                    </button>
                    <img
                      v-if="isImageFile(manualOrderData.po_document.type)"
                      :src="getFilePreview(manualOrderData.po_document)"
                      class="object-cover w-full h-full"
                      alt="Preview"
                    />
                    <div v-else class="flex items-center justify-center w-full h-full bg-gray-100">
                      <span class="p-2 text-xs text-center break-words">{{
                        manualOrderData.po_document?.name
                      }}</span>
                    </div>
                  </div>
                </div>
                <!-- Customer Select -->
                <div>
                  <label for="user-select" class="block mb-1 text-sm font-medium text-gray-700"
                    >Order Type</label
                  >
                  <Select
                    append-to-body
                    v-model="manualOrderData.order_type"
                    :options="order_types"
                    label="label"
                    placeholder="Order Type"
                    :clearable="false"
                    @option:selected="handleOrderTypeUpdate"
                    :class="{ 'border-red-500': errorMessages['order_type'] }"
                  />
                  <span v-if="errorMessages['order_type']" class="text-xs text-red-500">{{
                    errorMessages['order_type']
                  }}</span>
                </div>
                <!-- General Comment -->
                <div>
                  <label for="general-comment" class="block mb-1 text-sm font-medium text-gray-700"
                    >General Comment</label
                  >
                  <textarea
                    v-model="manualOrderData.general_comments"
                    id="general-comment"
                    placeholder="Enter any general comments"
                    rows="3"
                    class="input"
                  ></textarea>
                </div>
              </div>
              <!-- Shipping Address Section -->
              <div class="mt-4">
                <label
                  for="shipping-address-select"
                  class="block mb-1 text-sm font-medium text-gray-700"
                >
                  Shipping Address <span class="text-red-500">*</span>
                </label>

                <Select
                  append-to-body
                  v-model="manualOrderData.shipping_address"
                  :options="customerAddresses"
                  label="address1"
                  placeholder="Select Address"
                  :clearable="false"
                  :disabled="is_custom_address"
                  :class="{
                    'border-red-500': errorMessages['shipping_address'],
                  }"
                />
                <span v-if="errorMessages['shipping_address']" class="text-xs text-red-500">{{
                  errorMessages['shipping_address']
                }}</span>

               <div v-if="manualOrderData.shipping_address && !is_custom_address" class="p-4 mt-4 border rounded-md">
                  <!-- New address form fields -->
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.first_name"
                        disabled
                        placeholder="First Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.first_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.first_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.first_name'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.last_name"
                        disabled
                        placeholder="Last Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.last_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.last_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.last_name'] }}</span
                      >
                    </div>
                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.email"
                        disabled
                        placeholder="Email"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.email'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.email']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.email'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.address1"
                        disabled
                        placeholder="Address"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.address1'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.address1']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.address1'] }}</span
                      >
                    </div>

                    <input
                      v-model="manualOrderData.shipping_address.address2"
                      disabled
                      placeholder="Second Address"
                      class="input"
                    />
                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.phone_number"
                        disabled
                        placeholder="Phone Number"
                        class="input"
                      />
                      <span
                        v-if="errorMessages['shipping_address.phone_number']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.phone_number'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.city"
                        disabled
                        placeholder="City"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.city'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.city']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.city'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.zip_code"
                        disabled
                        placeholder="Zip Code"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.zip_code'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.zip_code']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.zip_code'] }}</span
                      >
                    </div>

                    <div>
                      <Select
                        v-model="manualOrderData.shipping_address.country_id"
                        disabled
                        :class="{
                          'border-red-500': errorMessages['shipping_address.country_id'],
                        }"
                        :options="formattedCountries"
                        track-by="country_id"
                        label="name"
                        :reduce="(country) => country.country_id"
                        append-to-body
                        placeholder="Select country"
                      />
                      <span
                        v-if="errorMessages['shipping_address.country_id']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.country_id'] }}</span
                      >
                    </div>
                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.company_name"
                        disabled
                        placeholder="Company Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.company_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.company_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.company_name'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.shipping_address.state"
                        disabled
                        placeholder="State/Province"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['shipping_address.state'],
                        }"
                      />
                      <span
                        v-if="errorMessages['shipping_address.state']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['shipping_address.state'] }}</span
                      >
                    </div>
                  </div>

                </div>

              </div>
              <!-- New Shipping Address Section -->
              <div class="mt-4">
                <label class="inline-flex items-center">
                  <Switch
                    @update:modelValue="(e) => (is_custom_address = e)"
                    :checked="is_custom_address"
                    id="is_custom_address"
                    type="checkbox"
                    title=""
                    name="shipping_address"
                  />
                  <span class="ml-2">Enter a new shipping address</span>
                </label>
                <div v-if="is_custom_address" class="p-4 mt-4 border rounded-md">
                  <!-- New address form fields -->
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.first_name"
                        placeholder="First Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.first_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.first_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.first_name'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.last_name"
                        placeholder="Last Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.last_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.last_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.last_name'] }}</span
                      >
                    </div>
                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.email"
                        placeholder="Email"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.email'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.email']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.email'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.address1"
                        placeholder="Address"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.address1'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.address1']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.address1'] }}</span
                      >
                    </div>

                    <input
                      v-model="manualOrderData.custom_shipping_address.address2"
                      placeholder="Second Address"
                      class="input"
                    />
                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.phone_number"
                        placeholder="Phone Number"
                        class="input"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.phone_number']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.phone_number'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.city"
                        placeholder="City"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.city'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.city']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.city'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.zip_code"
                        placeholder="Zip Code"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.zip_code'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.zip_code']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.zip_code'] }}</span
                      >
                    </div>

                    <div>
                      <Select
                        v-model="manualOrderData.custom_shipping_address.country_id"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.country_id'],
                        }"
                        :options="formattedCountries"
                        track-by="country_id"
                        label="name"
                        :reduce="(country) => country.country_id"
                        append-to-body
                        placeholder="Select country"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.country_id']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.country_id'] }}</span
                      >
                    </div>
                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.company_name"
                        placeholder="Company Name"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.company_name'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.company_name']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.company_name'] }}</span
                      >
                    </div>

                    <div>
                      <input
                        v-model="manualOrderData.custom_shipping_address.state"
                        placeholder="State/Province"
                        class="input"
                        :class="{
                          'border-red-500': errorMessages['custom_shipping_address.state'],
                        }"
                      />
                      <span
                        v-if="errorMessages['custom_shipping_address.state']"
                        class="text-xs text-red-500"
                        >{{ errorMessages['custom_shipping_address.state'] }}</span
                      >
                    </div>
                  </div>
                  <Switch
                    class='mt-4'
                    @update:modelValue="(e) => (manualOrderData.save_address = e)"
                    :checked="manualOrderData.save_address"
                    id="save_address"
                    type="checkbox"
                    title="Save new shipping address"
                    name="save_address"
                  />
                </div>
              </div>
            </template>
          </Accordion>
          <!-- Product Sections -->
          <template v-if="manualOrderData.merchant">
            <!-- <FieldArray name="factory_products" :key="forceUpdateKey"> -->
            <div
            v-for="(factoryProduct, index) in manualOrderData.factory_products"
              :key="factoryProduct._uuid"
              :id="`product-${factoryProduct._uuid}`"
              :ref="el => productAccordionRefs[index] = el"
            >
            <Accordion
              :open="!!activeAccordionIndex[index]"
              @toggle="handleAccordionToggle(index)"
            >
              <template #header>
                <div
                  class="flex flex-col"
                  :class="{
                    'text-red-500': submitCount > 0 && hasProductErrors(errorMessages, index),
                  }"
                >
                  <div class="flex">
                    <span
                      v-if="index > 0"
                      @click.stop="removeProductFromOrder(index)"
                      class="mr-2 text-red-500 cursor-pointer"
                    >
                      <TrashIcon class="w-5 h-5" />
                    </span>
                    {{ `Product ${index + 1}` }}
                     <span v-if="factoryProduct.is_new_product && factoryProduct.new_product_name">
                      : {{ factoryProduct.new_product_name }}
                    </span>
                    <span v-else-if="factoryProduct?.product?.sku_id">
                      : {{ factoryProduct?.product?.sku_id ?? factoryProduct?.product?.sku_id  }}
                    </span>
                  </div>

                  <span
                    v-if="submitCount > 0 && hasProductErrors(errorMessages, index)"
                    class="text-sm font-normal"
                  >
                    Some fields couldn't be validated
                  </span>
                </div>
              </template>
              <template #content>
                <!-- Product form fields -->
                <Switch
                  @update:modelValue="(e) => (factoryProduct.is_new_product = e)"
                  :checked="factoryProduct.is_new_product"
                  :id="`custom_product_${index}`"
                  type="checkbox"
                  title="Custom Product"
                  :name="`product_${index}`"
                />
                <div class="grid grid-cols-2 gap-4">
                  <template v-if="factoryProduct.is_new_product">
                    <div>
                      <label class="block mb-1 text-sm font-medium text-gray-700"
                        >Product Name
                      </label>
                      <input
                        v-model="factoryProduct.new_product_name"
                        type="text"
                        placeholder="Custom Product Name"
                        class="input"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <div>
                      <label class="block mb-1 text-sm font-medium text-gray-700"
                        >Select Product / SKU
                      </label>
                      <Select
                        v-model="factoryProduct.product"
                        append-to-body
                        :options="products"
                        label="sku_id"
                        track-by="id"
                        placeholder="Select Product"
                        :clearable="false"
                        @option:selected="
                          (option) => {
                            handleProductUpdate(option, index);
                            manualOrderData.factory_products[index] = {
                              ...manualOrderData.factory_products[index],
                              product: option,
                            };
                          }
                        "
                      />
                    </div>
                    <div>
                      <label class="block mb-1 text-sm font-medium text-gray-700"
                        >Select Style
                      </label>

                      <Select
                        :modelValue="manualOrderData.factory_products[index].style"
                        @update:modelValue="(val) => handleValueChange(index, val, 'style')"
                        append-to-body
                        :options="
                          manualOrderData.factory_products[index].product?.productstyles || []
                        "
                        label="name"
                        placeholder="Select Style"
                        @option:selected="
                          (option) => {
                            manualOrderData.factory_products[index] = {
                              ...manualOrderData.factory_products[index],
                              style: option,
                            };
                          }
                        "
                        :clearable="false"
                      />
                    </div>
                    <div>
                      <label class="block mb-1 text-sm font-medium text-gray-700"
                        >Select Addons</label
                      >
                      <Select
                        v-model="manualOrderData.factory_products[index].addons"
                        append-to-body
                        :options="addons"
                        multiple
                        label="title"
                        placeholder="Select Addons"
                        @option:selected="handleAddonsSelection($event, index)"
                      />
                    </div>
                  </template>
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700"
                      >Design Nickname</label
                    >
                    <input
                      v-model="factoryProduct.design_nick_name"
                      type="text"
                      placeholder="Design nickname"
                      class="input"
                    />
                  </div>
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700">Design ID</label>
                    <input
                      v-model="factoryProduct.design_id"
                      type="text"
                      placeholder="Design Id"
                      class="input"
                    />
                  </div>
                  <div></div>

                  <!-- Design Assets -->
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700"
                      >Design Assets</label
                    >
                    <div v-if="index === 0" class="mb-2">
                      <label class="inline-flex items-center">
                        <input
                          type="checkbox"
                          v-model="manualOrderData.is_common_assets"
                          @change="handleProductCommonDataUpdate($event, 'assets')"
                          class="text-indigo-600 border-gray-300 rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span class="ml-2">Use these assets for all products</span>
                      </label>
                    </div>
                    <div v-if="index !== 0 && manualOrderData.is_common_assets">
                      <p class="text-sm text-gray-500">First product assets being used</p>
                    </div>
                    <div v-else>
                      <div>
                        <CommonFileUploader
                          accept=".png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip"
                          fieldName="assets"
                          :index="index"
                          :multiple="true"
                          outerClass="w-full"
                          @file-change="(data) => handleFileUpload({target:{files:data.files, type:'file'}}, data.fieldName, data.index, '.png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip')"
                          @focus="focusedField = $event"
                          @blur="focusedField = null"
                        />
                      </div>
                      <div
                        v-if="
                          manualOrderData.factory_products?.[index]?.assets_info &&
                          manualOrderData.factory_products?.[index]?.assets_info.length > 0
                        "
                        class="flex flex-wrap gap-2 mt-2"
                      >
                        <div
                          v-for="(assetInfo, assetIndex) in manualOrderData.factory_products[index]
                            .assets_info"
                          :key="assetIndex"
                          class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden"
                        >
                          <button
                            @click="removeFactoryProductAsset(index, assetIndex)"
                            class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                          <img
                            v-if="isImageFile(assetInfo.extension)"
                            :src="getFilePreview(assetInfo.path)"
                            class="w-full h-full object-cover"
                            alt="Preview"
                          />
                          <div
                            v-else
                            class="flex items-center justify-center w-full h-full bg-gray-100"
                          >
                            <span class="p-2 text-xs text-center break-words">{{
                              assetInfo.name
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Upload Roster -->
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700"
                      >Upload Roster
                    </label>
                    <div v-if="index !== 0 && manualOrderData.is_common_rosters">
                      <p class="text-sm text-gray-500">First product roster being used</p>
                    </div>
                    <div v-else>
                      <div v-if="index === 0" class="flex flex-wrap justify-between gap-2 mb-2">
                        <label class="inline-flex items-center">
                          <input
                            type="checkbox"
                            v-model="manualOrderData.is_common_rosters"
                            @change="handleProductCommonDataUpdate($event, 'roster')"
                            class="text-indigo-600 border-gray-300 rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span class="ml-2">Use this roster for all products</span>
                        </label>
                        <label class="inline-flex items-center">
                          <input
                            :disabled="
                              !manualOrderData.factory_products[index]?.product &&
                              !factoryProduct.is_new_product
                            "
                            type="checkbox"
                            v-model="manualOrderData.factory_products[index].enable_manual_roster"
                            class="text-indigo-600 border-gray-300 rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span class="ml-2">Enter Manual Roster</span>
                        </label>
                      </div>
                      <div v-else class="mb-2">
                        <label class="inline-flex items-center">
                          <input
                            :disabled="
                              !manualOrderData.factory_products[index]?.product &&
                              !factoryProduct.is_new_product
                            "
                            type="checkbox"
                            v-model="manualOrderData.factory_products[index].enable_manual_roster"
                            class="text-indigo-600 border-gray-300 rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span class="ml-2">Enter Manual Roster</span>
                        </label>
                      </div>
                      <div
                        class="flex items-center"
                        :class="`${manualOrderData.factory_products[index].is_new_product}`"
                      >

                        <div class="flex-1">
                        <CommonFileUploader
                          accept=".xlsx,.xls"
                          fieldName="roster_file"
                          :index="index"
                          :disabled="isDisabled(index)"
                          outerClass="w-full"
                          @file-change="(data) => {
                          handleFileUpload({target:{files:data.files, type:'file'}}, data.fieldName, data.index, '.xlsx,.xls')
                          }"
                          @focus="focusedField = $event"
                          @blur="focusedField = null"
                        />
                        </div>
                        <button
                          v-if="manualOrderData.factory_products?.[index]?.product"
                          type="button"
                          @click="
                            downloadTemplate(
                              manualOrderData.merchant.id,
                              manualOrderData.factory_products?.[index]?.product.id,
                              manualOrderData.factory_products?.[index]?.product.sku_id
                            )
                          "
                          class="p-2 ml-2 text-indigo-700 bg-indigo-100 rounded-full hover:bg-indigo-200"
                        >
                          <ArrowDownTrayIcon class="w-5 h-5" />
                        </button>
                      </div>
                      <div class="text-right mt-1">
                        <span class="inline-block cursor-pointer text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark active:bg-primary transition"
                        v-if="copiedRoster.isAnyRosterCopied
                        && copiedRoster.copiedFromProductIndex !== index
                        && !manualOrderData.is_common_rosters
                        && (manualOrderData.factory_products[index]?.product ||
                              factoryProduct.is_new_product)" @click="pasteRosterToProduct(copiedRoster,index)">Paste Roster from product {{ Number(copiedRoster.copiedFromProductIndex) + 1 }}</span>
                      </div>
                      <!-- Roster file preview -->
                      <template v-if="manualOrderData.factory_products?.[index]?.roster_file">
                        <div
                          class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden mt-2"
                        >
                          <button
                            class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                            @click="resetRosterData(index)"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                          <div class="flex items-center justify-center w-full h-full bg-gray-100">
                            <span class="p-2 text-xs text-center break-words">{{
                              manualOrderData.factory_products?.[index].roster_file.name
                            }}</span>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- Front Image Upload -->
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700"
                      >Upload Front Image</label
                    >
                    <div>
                      <CommonFileUploader
                        accept=".png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip"
                        fieldName="front_image"
                        :index="index"
                        outerClass="w-full"
                        @file-change="(data) =>
                          handleFileUpload({target:{files:data.files, type:'file'}}, data.fieldName, data.index, '.png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip')
                          "
                        @focus="focusedField = $event"
                        @blur="focusedField = null"
                      />
                    </div>
                    <!-- Front image preview -->
                    <template v-if="manualOrderData.factory_products?.[index]?.front_image">
                      <div
                        class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden mt-2"
                      >
                        <button
                          class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                          @click="removeFile(index, 'front_image')"
                        >
                          <TrashIcon class="w-4 h-4" />
                        </button>
                        <img
                          :src="
                            getFilePreview(manualOrderData.factory_products?.[index]?.front_image)
                          "
                          class="object-cover w-full h-full"
                          alt="Preview"
                        />
                      </div>
                    </template>
                  </div>

                  <!-- Back Image Upload -->
                  <div>
                    <label class="block mb-1 text-sm font-medium text-gray-700"
                      >Upload Back Image</label
                    >
                    <div>
                      <CommonFileUploader
                        accept=".png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip"
                        fieldName="back_image"
                        :index="index"
                        outerClass="w-full"
                        @file-change="(data) => handleFileUpload({target:{files:data.files, type:'file'}}, data.fieldName, data.index, '.png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip')"
                        @focus="focusedField = $event"
                        @blur="focusedField = null"
                      />
                    </div>
                    <!-- Back image preview -->
                    <template v-if="manualOrderData.factory_products?.[index]?.back_image">
                      <div
                        class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden mt-2"
                      >
                        <button
                          class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                          @click="removeFile(index, 'back_image')"
                        >
                          <TrashIcon class="w-4 h-4" />
                        </button>
                        <img
                          :src="
                            getFilePreview(manualOrderData.factory_products?.[index]?.back_image)
                          "
                          class="object-cover w-full h-full"
                          alt="Preview"
                        />
                      </div>
                    </template>
                  </div>

                  <Accordion
                    class="col-span-2"
                    v-if="manualOrderData.factory_products[index].enable_manual_roster"
                    :defaultOpen="true"
                  >
                    <template #header> Roster Information </template>
                    <template #content>
                      <template v-if="index !== 0 && manualOrderData.is_common_rosters">
                        here
                      </template>
                      <ManualRoster
                        v-else
                        :players="manualOrderData.factory_products[index].roster_data"
                        :product_sizes="getProductSizes(index)"
                        :isCustomProduct="manualOrderData.factory_products[index].is_new_product"
                        :errorMessages="errorMessages"
                        :index="index"
                        :key="`${'manual_roster_' + index}`"
                        @update:players="() => handleManualRosterUpdate(index)"
                        @copy:roster="(roster) => copyRoster(index, roster)"
                        :copiedRoster="copiedRoster"
                      />
                    </template>
                  </Accordion>
                </div>
              </template>
            </Accordion>
            </div>
            <!-- </FieldArray> -->
          </template>

          <!-- Order Summary -->
          <Accordion v-if="manualOrderData.factory_products.length > 0" :default-open="true">
            <template #header> Order Summary </template>
            <template #content>
              <div class="w-full overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        v-for="header in headers"
                        :key="header.key"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                      >
                        {{ header.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(item, itemIndex) in manualOrderData.factory_products"
                      :key="itemIndex"
                      class="cursor-pointer hover:bg-blue-50 transition-colors"
                      @click="navigateToProduct(itemIndex)"
                    >
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span v-if="item.is_new_product">{{ item.new_product_name }}</span>
                        <span v-else-if="item.product && item.product.sku">{{
                          item.product.sku_id
                        }}</span>
                      </td>
                      <td class="px-6 py-4">
                        <span v-if="item.design_nick_name">{{ item.design_nick_name }}</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex flex-wrap gap-1">
                          <template v-for="(roster, rosterIndex) in item.roster_data" :key="rosterIndex">
                            <span
                              v-if="roster?.size"
                              class="px-2 py-1 text-xs text-gray-700 bg-gray-200 rounded-full max-w-[300px]"
                              :class="{
                                '!rounded-[5px] whitespace-normal': roster?.size.length > 30,
                              }"
                            >
                              {{ roster?.size }}: {{ roster?.quantity }}
                            </span>
                          </template>
                        </div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        {{ item.product_price_object ? item.product_price_object.quantity : 'n/A' }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          placeholder="Enter Price"
                          :class="{
                            'w-[70px]' : !item.product_price,
                            'w-[50px]' : item.product_price
                          }"
                          :value="item.product_price"
                          @change="($event) => {
                            const input = $event.target as HTMLInputElement;
                            const sanitized = input.value.replace(',', '.');
                            const number = formatToNumber(sanitized)
                            input.value = number
                            item.product_price = number
                          }"
                        >
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <!-- v-if="
                            !item.is_new_product &&
                            item.product &&
                            item.product.sku &&
                            item.product_price_object &&
                            item.product_price_object.quantity
                          " -->
                        <span
                        >
                        {{
                          formatToNumber(item.product_price_object.quantity * parseFloat(item.product_price))
                        }}
                          <!-- {{

                            item.product_price_object
                              ? (
                                  parseFloat(item.product_price_object.quantity.toString()) *
                                  getNetPrice(item.product.sku.net_price)
                                ).toFixed(2)
                              : 'N/A'
                          }} -->
                        </span>
                        <!-- <span v-else>0.00</span> -->
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </Accordion>

          <button
            type="button"
            @click="addProduct"
            class="px-4 py-2 mt-4 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Product
          </button>
        </div>
      </Form>
    </template>
  </Dialog>


  <Dialog
    v-if="showQuickChooser"
    title="Choose where to upload pasted file"
    :sm="true"
    :show-close-button="true"
    @closeEvent="() => { showQuickChooser = false; pastedFiles = []; }"
  >
    <template #modal-content>
      <div class="flex flex-col gap-4">
        <!-- PO Document -->
        <div class="rounded border border-gray-200 bg-white p-3 mb-2">
          <div class="font-semibold text-gray-700 mb-2">General</div>
          <button
            class="w-full px-4 py-2 rounded bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300"
            @click="() => { handleFileUpload({ target: { files: pastedFiles, type: 'file' } }, 'po_document', null, '.pdf'); showQuickChooser = false; pastedFiles = []; }"
          >
            Upload to PO Document
          </button>
        </div>
        <template v-if="manualOrderData.merchant">
        <Accordion
          v-for="(product, idx) in manualOrderData.factory_products"
          :key="idx"
          :defaultOpen="true"
          class="mb-2 border border-gray-200 rounded bg-white shadow-sm"
        >
          <template #header>
            <div class="flex items-center px-4 cursor-pointer">
              {{ `Product ${idx + 1}` }}
              <span v-if="product.is_new_product && product.new_product_name">
                : {{ product.new_product_name }}
              </span>
              <span v-else-if="product?.product?.sku_id">
                : {{ product?.product?.sku_id ?? product?.product?.sku_id  }}
              </span>
            </div>
          </template>
          <template #content>
            <div class="grid grid-cols-3 gap-2">
              <button
                v-if="quickChooserSupportedFields.assets"
                class="w-full px-3 py-2 rounded bg-gray-50 text-gray-700 hover:bg-blue-100 border border-gray-300"
                @click="() => {
                  shouldScrollToAccordion = true;
                  activeAccordionIndex[idx] = true;
                  handleFileUpload({ target: { files: pastedFiles, type: 'file' } }, 'assets', idx);
                  showQuickChooser = false;
                  pastedFiles = [];
                }"
              >
                Design Assets
              </button>
              <button
                v-if="quickChooserSupportedFields.roster_file"
                class="w-full px-3 py-2 rounded bg-gray-50 text-gray-700 hover:bg-green-100 border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!manualOrderData.factory_products[idx]?.product && !product.is_new_product"
                @click="() => {
                  shouldScrollToAccordion = true;
                  activeAccordionIndex[idx] = true;
                  handleFileUpload({ target: { files: pastedFiles, type: 'file' } }, 'roster_file', idx);
                  showQuickChooser = false;
                  pastedFiles = [];
                }"
              >
                Roster File
              </button>
              <button
                v-if="quickChooserSupportedFields.front_image"
                class="w-full px-3 py-2 rounded bg-gray-50 text-gray-700 hover:bg-purple-100 border border-gray-300"
                @click="() => {
                  shouldScrollToAccordion = true;
                  activeAccordionIndex[idx] = true;
                  handleFileUpload({ target: { files: pastedFiles, type: 'file' } }, 'front_image', idx);
                  showQuickChooser = false;
                  pastedFiles = [];
                }"
              >
                Front Image
              </button>
              <button
                v-if="quickChooserSupportedFields.back_image"
                class="w-full px-3 py-2 rounded bg-gray-50 text-gray-700 hover:bg-pink-100 border border-gray-300"
                @click="() => {
                  shouldScrollToAccordion = true;
                  activeAccordionIndex[idx] = true;
                  handleFileUpload({ target: { files: pastedFiles, type: 'file' } }, 'back_image', idx);
                  showQuickChooser = false;
                  pastedFiles = [];
                }"
              >
                Back Image
              </button>
            </div>
          </template>
        </Accordion>
        </template>
      </div>
    </template>
  </Dialog>

  <create-customer-modal
    v-if="showCreateCustomer"
    :show="showCreateCustomer"
    :company_id="manualOrderData.merchant ? manualOrderData.merchant.id : null"
    @close="handleCustomerCreateClose"
    @customerCreated="handleCustomerCreated"
  />

</template>

<script setup lang="ts">
import Accordion from '@/components/Shared/Accordion.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import ManualRoster from '@/components/Shared/ManualRoster.vue';
import Select from '@/components/Shared/Select.vue';
import Switch from '@/components/Shared/Switch.vue';
import { showTimerAlert, showConfirm, showToast } from '@/composables/swal';
import { formatToNumber } from '@/composables';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import {
  ActiveAddon,
  Addon,
  AddressModel,
  AssetInfo,
  Company,
  Country,
  Customer,
  ManualOrderData,
  NetPrice,
  OrderTotalPrice,
  OrderType,
  Product,
  ProductPriceObject,
  RosterData,
  Style,
} from '@/models';
import { useMainStore } from '@/store';
import { ArrowDownTrayIcon, TrashIcon, PlusIcon } from '@heroicons/vue/24/outline';
import unset from 'lodash/unset';
import { storeToRefs } from 'pinia';
import readXlsxFile from 'read-excel-file';
import { Form, useFieldArray, useForm } from 'vee-validate';
import { ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import CommonFileUploader from '@/components/Shared/CommonFileUploader.vue';
import { QuickChooserSupport } from '@/models/QuickChooser';
import { CopiedRoster } from '@/models/CopiedRoster';
import CreateCustomerModal from '@/components/Dashboard/Modals/CreateCustomerModal.vue';
import { Input } from 'postcss';

const emit = defineEmits<{
  (e: 'onSave'): void;
  (e: 'close'): void;
}>();

const newCustomerDefaultObject:Customer = {
  id: 0,
  name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  company_name: '',
  addresses: [],
};
const showCreateCustomer = ref<boolean>(false);

const companies = ref<Company[]>([]);
const customers = ref<Customer[]>([]);
const products = ref<Product[]>([]);
const styles = ref<Style[]>([]);
const addons = ref<ActiveAddon[]>([]);
const customerAddresses = ref<AddressModel[]>([]);
const companyCurrency = ref<NetPrice | null>(null);
const countries = ref<Country[]>([]);
const manualOrderData = reactive<ManualOrderData>(getManualOrderDataDefaultObject());
const uploadSessionUUID = ref<string | null>(null);
const uploadingFiles = ref<boolean>(false);
const storageUrl = import.meta.env.VITE_APP_FILE_URL;
const focusedField = ref(null);
const showQuickChooser = ref(false);
const pastedFiles = ref<File[]>([]);
const activeAccordionIndex = ref<Record<number, boolean>>({ 0: true })
const productAccordionRefs = ref<Record<number, Element | ComponentPublicInstance | null>>({})
const shouldScrollToAccordion = ref(false);
const scrollToProductUuid = ref<string | null>(null);
const copiedRoster = ref<CopiedRoster>({isAnyRosterCopied: false ,copiedFromProductIndex: null, rosterData: []});

onMounted(() => {
  window.addEventListener("paste", handlePaste);
});
onBeforeUnmount(() => {
  window.removeEventListener("paste", handlePaste);
});
function getProductSizes(index): string[] | Array<any> {
  if (
    manualOrderData.factory_products[index].product &&
    manualOrderData.factory_products[index].product.sizes &&
    manualOrderData.factory_products[index].product.sizes.length > 0 &&
    Array.isArray(manualOrderData.factory_products[index].product.sizes[0].json_data)
  ) {
    return manualOrderData.factory_products[index].product.sizes[0].json_data.map(
      (size) => size.name
    );
  }
  return [];
}

const order_types = ref<OrderType[]>([
  {
    symbol: 'Ri',
    label: 'Remake (with invoice)',
    default: false,
  },
  {
    symbol: 'Si',
    label: 'Sample (with invoice)',
    default: false,
  },
  {
    symbol: 'Oi',
    label: 'Order (with invoice)',
    default: true,
  },
  // {
  //   symbol: "Vi",
  //   label: "Vanx (with invoice)",
  //   default: false,
  // },
  {
    symbol: 'R',
    label: 'Remake (no invoice)',
    default: false,
  },
  {
    symbol: 'Si',
    label: 'Sample (no invoice)',
    default: false,
  },
  {
    symbol: 'Ci',
    label: 'Compensation (no invoice)',
    default: false,
  },
  // {
  //   symbol: "V",
  //   label: "Vanx (no invoice)",
  //   default: false,
  // },
]);
const orderTotalPrice = ref<OrderTotalPrice>(getOrderTotalPrice());
const is_custom_address = ref<boolean>(false);
const is_new_product = ref<boolean[]>([false]);
const { setFieldValue } = useForm();
const { loading } = storeToRefs(useMainStore());
const { fields: factory_products } = useFieldArray('factory_products');

const factoryProductSchema = yup.object({
  is_new_product: yup.boolean().default(false),
  product: yup.object().nullable().optional(),
  style: yup.object().nullable().optional(),
  addons: yup.array().nullable().optional(),
  new_product_name: yup.string().nullable().optional(),
  design_nick_name: yup.string().nullable().optional(),
  design_id: yup.string().nullable().optional(),
});

// Main form schema
const schema = yup.object({
  is_custom_address: yup.boolean().default(false),
  merchant: yup.object().required('Merchant is required'),
  customer: yup.object().required('Customer is required'),
  customer_reference_no: yup.string().nullable().optional(),
  po_number: yup.string().nullable().optional(),
  po_document: yup.string().nullable().optional(),
  shipping_address: yup.object().when('is_custom_address', {
    is: true,
    then: () => yup.object().nullable().notRequired(),
    otherwise: () => yup.object().nullable().required('Shipping address is required'),
  }),
  custom_shipping_address: yup.object().when('is_custom_address', {
    is: true,
    then: () =>
      yup.object().shape({
        first_name: yup.string().required('First name is required'),
        last_name: yup.string().required('Last name is required'),
        email: yup.string().email('Invalid email').required('Email is required'),
        address1: yup.string().required('Address is required'),
        address2: yup.string().nullable().optional(),
        city: yup.string().required('City is required'),
        phone_number: yup.string().required('Phone number is required'),
        zip_code: yup.string().required('Zip code is required'),
        country_id: yup.string().required('Country is required'),
        company_name: yup.string().required('Company name is required'),
        state: yup.string().when('country_id',{
          is: (val) => {
            const country = countries.value.find((c) => c.id == val);
            return country && country.states && country.states.length > 0;
          },
          then: () => yup.string().required('State is required'),
          otherwise: () => yup.string().notRequired(),
        })
      }),
    otherwise: () => yup.object().notRequired(),
  }),
  factory_products: yup.array().of(factoryProductSchema).min(1, 'At least one product is required'),
});

const formattedCountries = computed(() => {
  return countries.value.map((country) => ({
    country_id: country.id,
    name: country.name,
    states: country.states,
  }));
});

const headers = [
  { key: 'sku', label: 'SKU' },
  { key: 'design_nick_name', label: 'Design Name' },
  { key: 'size', label: 'Size' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'price', label: 'Price' },
  { key: 'amount', label: 'Amount' },
];
// Reactive object to hold errors
const errorMessages = reactive<Record<string, string>>({});

// Methods
function handleManualRosterUpdate(factory_product_index) {
  setCommonRosterData();
  setProductPriceObject(factory_product_index);
}
function copyRoster(factory_product_index: number, roster: RosterData[]) {
  if(copiedRoster.value.isAnyRosterCopied) {
    showConfirm(
      'Overwrite existing copied roster?',
      'You already have a roster copied. Do you want to copy this product’s roster instead?',
    ).then((result) => {
      if (result) {
        setCopiedRoster(factory_product_index, roster);
        showToast('Roster copied successfully', 'success');
      }
    });
  } else {
    setCopiedRoster(factory_product_index, roster);
    showToast('Roster copied successfully', 'success');
  }
}
function setCopiedRoster(factory_product_index: number, roster: RosterData[]) {
  copiedRoster.value.isAnyRosterCopied = true;
  copiedRoster.value.copiedFromProductIndex = factory_product_index;
  copiedRoster.value.rosterData = roster;
}

function pasteRosterToProduct(copiedRoster: CopiedRoster, target_product_index: number) {
  let product_sizes = getProductSizes(target_product_index)
  let isCustomProduct = manualOrderData.factory_products[target_product_index].is_new_product;
  let rosterData =  copiedRoster.rosterData.map(player => {

      let matchedSize = '';
      if(product_sizes.length > 0) {
        product_sizes.forEach(size => {
          if(size === player.size) {
            matchedSize = size;
          }
        });
      }
      else if(isCustomProduct) {

        matchedSize = player.size;
      }
      return {
        ...player,
        size: matchedSize
      };
    });
  let rosterItems = JSON.stringify(rosterData);
  manualOrderData.factory_products[target_product_index].roster_data = JSON.parse(rosterItems);
  handleManualRosterUpdate(target_product_index);
  showToast('Roster pasted successfully', 'success');
}

function getManualOrderDataDefaultObject(
  updatedKeys: Partial<ManualOrderData> = {}
): ManualOrderData {
  const defaultObject: ManualOrderData = {
    merchant: null,
    customer: null,
    customer_reference_no: null,
    po_number: null,
    po_document: null,
    order_type: {
      symbol: '',
      default: false,
      label: '',
    },
    general_comments: '',
    is_custom_address: false,
    save_address: false,
    custom_shipping_address: {
      id: 0,
      first_name: '',
      last_name: '',
      email: '',
      address1: '',
      address2: '',
      phone_number: '',
      city: '',
      zip_code: '',
      country: '',
      company_name: '',
      state: '',
      country_id: '',

    },
    shipping_address: null,
    is_common_assets: false,
    is_common_rosters: false,
    factory_products: [
      {
        factory_id: 0,
        product: {
          sku: null,
        },
        is_new_product: false,
        new_product_name: '',
        style: null,
        addons: [],
        design_nick_name: '',
        design_id: '',
        assets: [],
        assets_info: [],
        roster_file: null,
        roster_data: [],
        product_price_object: getProductPriceDefaultObject(),
        front_image: null,
        enable_manual_roster: false,
        back_image: null,
        product_price: '0.00',
        _uuid: uuidv4(),
      },
    ],
  };
  return { ...defaultObject, ...updatedKeys };
}

function handleValueChange(index: number, val: any, column: string) {
  const keys = column.split('.'); // Split column by '.' to handle nested fields

  if (keys.length === 1) {
    // Simple case: Update top-level field (e.g., style)
    manualOrderData.factory_products[index][column] = val;
  } else {
    // Nested case: Update field inside a nested object (e.g., product.sku)
    let nestedObj = manualOrderData.factory_products[index];
    // Traverse through the keys, making sure each level exists
    keys.forEach((key, i) => {
      if (nestedObj[key] === null || nestedObj[key] === undefined) {
        // If the key is null or undefined, initialize an empty object or array
        if (i === keys.length - 1) {
          nestedObj[key] = val; // Set the value at the last key
        } else {
          nestedObj[key] = {}; // Initialize as an empty object for further traversal
        }
      } else {
        if (i === keys.length - 1) {
          nestedObj[key] = val; // Set the value at the last key
        } else {
          nestedObj = nestedObj[key]; // Traverse deeper into the object
        }
      }
    });
  }
}

function removeProductFromOrder(index: number) {
  if (manualOrderData.factory_products && manualOrderData.factory_products.length > index) {
    // Remove the product from the array
    manualOrderData.factory_products.splice(index, 1);

    // Remove error messages for this product
    const errorKeys = Object.keys(errorMessages).filter((key) =>
      key.startsWith(`factory_products[${index}]`)
    );

    // Delete each error message for this product
    errorKeys.forEach((key) => {
      delete errorMessages[key];
    });

    // Shift error messages for products after the removed one
    const remainingProducts = manualOrderData.factory_products.length;
    for (let i = index; i < remainingProducts; i++) {
      const oldKeyPrefix = `factory_products[${i + 1}]`;
      const newKeyPrefix = `factory_products[${i}]`;

      // Find all error messages for the next product
      const nextProductErrors = Object.keys(errorMessages).filter((key) =>
        key.startsWith(oldKeyPrefix)
      );

      // Move each error message to the new index
      nextProductErrors.forEach((oldKey) => {
        const newKey = oldKey.replace(oldKeyPrefix, newKeyPrefix);
        errorMessages[newKey] = errorMessages[oldKey];
        delete errorMessages[oldKey];
      });
    }
  }
}

const isDisabled = (index) => {
  return (
    !manualOrderData.factory_products?.[index]?.product &&
    !manualOrderData.factory_products[index].is_new_product
  );
};

function getOrderTotalPrice(): OrderTotalPrice {
  let orderPrice: OrderTotalPrice = {
    currency_code: '',
    currency_symbol: '',
    price: 0,
  };

  manualOrderData.factory_products.forEach((factoryProduct) => {
    if (!factoryProduct.is_new_product) {
      const { addons, product_price_object, product } = factoryProduct;
      if (product && product.sku && product.sku.net_price) {
        const productNetPrice = getNetPrice(product.sku.net_price);
        if (!orderPrice.currency_code) {
          orderPrice.currency_code = product.sku.net_price.code;
          orderPrice.currency_symbol = product.sku.net_price.symbol;
        }
        if (productNetPrice && product_price_object && product_price_object.quantity) {
          orderPrice.price += productNetPrice * parseInt(product_price_object.quantity.toString());
        }
      }
      if (addons && Array.isArray(addons)) {
        addons.forEach((addon: Addon) => {
          if (addon && addon.net_price) {
            const addonNetPrice = getNetPrice(addon.net_price);
            if (addonNetPrice && product_price_object && product_price_object.quantity) {
              orderPrice.price +=
                addonNetPrice * parseInt(product_price_object.quantity.toString());
            }
          }
        });
      }
    }
  });

  orderPrice.price = parseFloat(orderPrice.price.toFixed(2));
  return orderPrice;
}

function getProductPriceDefaultObject(): ProductPriceObject {
  const productPriceDefaultObject: ProductPriceObject = {
    currency_code: null,
    currency_symbol: null,
    product_price: 0,
    quantity: 0,
  };
  if (companyCurrency.value) {
    productPriceDefaultObject.currency_code = companyCurrency.value.code;
    productPriceDefaultObject.currency_symbol = companyCurrency.value.symbol;
  }
  return productPriceDefaultObject;
}

async function getManualOrderData(merchantId: number | null = null) {
  let manualOrderDataUrl = 'manual-order-data';
  if (merchantId) {
    manualOrderDataUrl += `?company_id=${merchantId}`;
  }
  try {
    const response = await http.get(manualOrderDataUrl);
    const { companies: fetchedCompanies, company_currency } = response.data.result;
    if (merchantId === null) {
      companies.value = fetchedCompanies;
    }
    companyCurrency.value = company_currency;
    let selectedMerchant: Company | null = null;
    if (fetchedCompanies.length > 0 && merchantId) {
      selectedMerchant = fetchedCompanies[0];
      customers.value = selectedMerchant
        ? [newCustomerDefaultObject, ...(selectedMerchant.customers || [])]
        : [newCustomerDefaultObject];
      products.value = selectedMerchant ? selectedMerchant.products : [];
    }
    manualOrderData.merchant = selectedMerchant;
    manualOrderData.order_type = order_types.value.find((type) => type.default);
  } catch (error: any) {
    handleResponseException(error);
  }
}
const addProduct = () => {
  const newProduct = {
    factory_id: 0,
    product: null,
    is_new_product: false,
    new_product_name: '',
    style: null,
    addons: [],
    design_nick_name: '',
    design_id: '',
    assets: [],
    assets_info: [],
    roster_file: null,
    roster_data: [],
    product_price_object: getProductPriceDefaultObject(),
    front_image: null,
    back_image: null,
    enable_manual_roster: false,
    product_price: '0.00',
    _uuid: uuidv4(),
  };

  if (!manualOrderData.merchant) {
    showTimerAlert('Missing info', 'Please select merchant', 'error');
    return;
  }
  if (!manualOrderData.customer) {
    showTimerAlert('Missing info', 'Please select customer', 'error');
    return;
  }
  manualOrderData.factory_products.push(newProduct);
  is_new_product.value.push(false);

  // Apply common assets/rosters if needed
  const lastIndex = manualOrderData.factory_products.length - 1;
  if (!manualOrderData.factory_products[lastIndex]) {
    manualOrderData.factory_products[lastIndex] = newProduct;
  }

  const firstProduct = manualOrderData.factory_products[0];
  if (!firstProduct) return;

  if (manualOrderData.is_common_assets && firstProduct.assets) {
    manualOrderData.factory_products[lastIndex].assets = [...firstProduct.assets];
    manualOrderData.factory_products[lastIndex].assets_info = firstProduct.assets_info
      ? [...firstProduct.assets_info]
      : [];
    setFieldValue(`factory_products.${lastIndex}.assets`, 'files-uploaded');
  }

  if (manualOrderData.is_common_rosters && firstProduct.roster_file) {
    manualOrderData.factory_products[lastIndex].roster_file = firstProduct.roster_file;
    manualOrderData.factory_products[lastIndex].roster_data = firstProduct.roster_data
      ? JSON.parse(JSON.stringify(firstProduct.roster_data))
      : [];
    setFieldValue(`factory_products.${lastIndex}.roster_file`, 'file-uploaded');
    setFieldValue(
      `factory_products.${lastIndex}.roster_data`,
      manualOrderData.factory_products[lastIndex].roster_data
    );
    setProductPriceObject(lastIndex);
  }
};

// FIXED: Added null checks to prevent errors
function setCommonRosterData() {
  const { is_common_rosters, is_common_assets } = manualOrderData;
  const firstProduct = manualOrderData.factory_products[0];
  if (!firstProduct) return;

  manualOrderData.factory_products.forEach((factoryProduct, index) => {
    if (index > 0 && factoryProduct) {
      if (is_common_rosters && firstProduct.roster_data) {
        factoryProduct.roster_data = JSON.parse(JSON.stringify(firstProduct.roster_data));
        factoryProduct.roster_file = firstProduct.roster_file;
        setFieldValue(`factory_products.${index}.roster_data`, factoryProduct.roster_data);
        setFieldValue(`factory_products.${index}.roster_file`, 'file-uploaded');
      }

      if (is_common_assets && firstProduct.assets) {
        factoryProduct.assets = [...firstProduct.assets];
        factoryProduct.assets_info = firstProduct.assets_info ? [...firstProduct.assets_info] : [];
        setFieldValue(`factory_products.${index}.assets`, 'files-uploaded');
      }

      setProductPriceObject(index);
    }
  });
}

async function handleMerchantUpdate(selectedMerchant: Company) {
  // if (factory_products.value.length > 0) {
  //   const confirm = await showConfirm(
  //     'Are you sure?',
  //     'This may remove the existing products from the list'
  //   );
  //   if (confirm) {
  //     setFieldValue('factory_products', getManualOrderDataDefaultObject().factory_products);
  //     is_new_product.value = [false];
  //     setCommonRosterData();
  //   }
  // } else {
    setFieldValue('factory_products', getManualOrderDataDefaultObject().factory_products);
    is_new_product.value = [false];
    setCommonRosterData();
  // }
  await getManualOrderData(selectedMerchant.id);
  manualOrderData.customer = null;
  manualOrderData.shipping_address = null;
  customerAddresses.value = [];
  manualOrderData.factory_products.forEach((factoryProduct: any, index: number) => {
    handleProductUpdate(null, index);
  });
}

function handleCustomerUpdate(selectedCustomer: Customer) {
  if(selectedCustomer.id === 0) {
    showCreateCustomer.value = true;
    return;
  }
  manualOrderData.shipping_address = null;
  customerAddresses.value = selectedCustomer.addresses || [];
  manualOrderData.custom_shipping_address.first_name = selectedCustomer.first_name || '';
  manualOrderData.custom_shipping_address.last_name = selectedCustomer.last_name || '';
  manualOrderData.custom_shipping_address.email = selectedCustomer.email || '';
  manualOrderData.custom_shipping_address.phone_number = selectedCustomer.phone_number || '';
  manualOrderData.custom_shipping_address.company_name = selectedCustomer.company_name || '';

  const defaultAddress = selectedCustomer.addresses.find(a => a.default === 1);
  if (defaultAddress) {
    manualOrderData.shipping_address = defaultAddress;
  }else{
    if (selectedCustomer.addresses) {
      manualOrderData.shipping_address = selectedCustomer.addresses[0] || null;
    }
  }
}

function handleOrderTypeUpdate(order_type: OrderType) {
  manualOrderData.order_type = order_type;
}

function handleProductUpdate(selectedProduct: Product | null, factoryProductIndex: number) {
  const factoryProduct = manualOrderData.factory_products[factoryProductIndex];
  if (!factoryProduct) return;
  manualOrderData.factory_products[factoryProductIndex].product_price =
    formatToNumber(selectedProduct?.sku?.net_price?.net_price || 0);;

  factoryProduct.style = null;
  factoryProduct.addons = [];
  resetRosterData(factoryProductIndex);
  if (selectedProduct) {
    // Check for both possible property names for styles
    styles.value = selectedProduct.productstyles || selectedProduct.productstyles || [];
    addons.value = selectedProduct.active_addons || [];
  } else {
    factoryProduct.product = null;
    styles.value = [];
    addons.value = [];
  }
  setProductPriceObject(factoryProductIndex);
}

async function downloadTemplate(companyId: number, productId: number, productName: string) {
  try {
    const response = await http.get(`template/download/${productId}/${companyId}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `product_${productName}_template.xlsx`;
    link.click();
  } catch (error: any) {
    handleResponseException(error);
    showTimerAlert('Error', 'Failed to download template', 'error');
  }
}
type SyntheticFileEvent = {
  target: {
    files: File[];
    type?: string;
  };
};

// helper for checking accepted files
const isAcceptedFile = (file: File, accept?: string): boolean => {
  if (!accept) return true;

  const acceptedTypes = accept.split(",").map(type => type.trim().toLowerCase());

  return acceptedTypes.some(type => {
    if (type.startsWith(".")) {
      return file.name.toLowerCase().endsWith(type);
    }
    if (type.endsWith("/*")) {
      return file.type.startsWith(type.replace("/*", ""));
    }
    return file.type.toLowerCase() === type;
  });
};
const handleFileUpload = async (
  event: Event | SyntheticFileEvent,
  fieldName: string,
  index?: number,
  accept?: string
): Promise<boolean> => {
  const target = event.target as HTMLInputElement;

  if (!target.files || target.files.length === 0) {
    return false;
  }
  const files = Array.from(target.files);
  const invalidFiles = files.filter(f => !isAcceptedFile(f, accept));
  if (invalidFiles.length > 0) {
    showToast(`Only ${accept} files are allowed`, "error");
    target.value = null;
    return false;
  }
  // Handle PO Document
  if (fieldName === 'po_document') {
    const file = target.files[0];
    manualOrderData.po_document = file;
    showToast('PO Document uploaded successfully', 'success');
    target.value = null;
    return true;
  }

  // Handle multiple assets
  if (
    (fieldName.includes('assets') ||
      fieldName.includes('front_image') ||
      fieldName.includes('back_image')) &&
    typeof index === 'number'
  ) {
    const files = Array.from(target.files);
    const factoryProduct = manualOrderData.factory_products?.[index];
    if (!factoryProduct) return false;

    // Determine asset type
    let assetType = 'assets';
    if (fieldName.includes('front_image')) assetType = 'front_image';
    if (fieldName.includes('back_image')) assetType = 'back_image';

    // Remove error if exists
    if (assetType === 'assets') {
      delete errorMessages[`factory_products[${index}].assets`];
    }

    // Generate UUID if not already present
    if (!uploadSessionUUID.value) {
      uploadSessionUUID.value = uuidv4();
    }

    showToast(
      assetType === 'assets'
        ? 'Uploading assets...'
        : assetType === 'front_image'
          ? 'Uploading front image...'
          : 'Uploading back image...',
      'info'
    );

        for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uuid', uploadSessionUUID.value);
      formData.append('type', assetType);

      try {
        uploadingFiles.value = true;
        const response = await http.post('/manual-order-assets', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const responseFile = response.data.file;

        // Update state based on asset type
        if (assetType === 'assets') {
          const assetInfo = {
            file: null, // Placeholder for file object if needed
            name: responseFile.name,
            size: responseFile.file_size,
            extension: responseFile.extension,
            path: responseFile.path,
            type: assetType,
          };
              // Deduplicate: only add if not already present (compare name+size+path)
              factoryProduct.assets_info = factoryProduct.assets_info || [];
              const exists = factoryProduct.assets_info.some((a: any) =>
                a.name === assetInfo.name && a.size === assetInfo.size && a.path === assetInfo.path
              );
              if (!exists) {
                factoryProduct.assets_info = [...factoryProduct.assets_info, assetInfo];
              }
              // Optionally, update assets array if you need to keep File objects
              factoryProduct.assets = factoryProduct.assets || [];
              const fileExists = factoryProduct.assets.some((f: File) => f.name === file.name && f.size === file.size);
              if (!fileExists) {
                factoryProduct.assets = [...factoryProduct.assets, file];
              }
        } else {
          // For images, just set the path
          factoryProduct[assetType] = responseFile.path;
        }
      } catch (error) {
        showTimerAlert('Upload Failed', `Failed to upload ${file.name}`, 'error');
      } finally {
        uploadingFiles.value = false;
      }
    }

    // If common assets is checked, propagate to all products
    if (assetType === 'assets' && manualOrderData.is_common_assets && index === 0) {
      manualOrderData.factory_products.forEach((product, idx) => {
        if (idx > 0 && product) {
          product.assets = [...factoryProduct.assets];
          product.assets_info = [...factoryProduct.assets_info];
          setFieldValue(`factory_products.${idx}.assets`, 'files-uploaded');
        }
      });
    }

    showToast(
      assetType === 'assets'
        ? 'Assets uploaded successfully'
        : assetType === 'front_image'
          ? 'Front image uploaded successfully'
          : 'Back image uploaded successfully',
      'success'
    );
    if (typeof index === 'number') {
      navigateToProduct(index);
    }
    return true;
  }

  // Handle roster file
  if (fieldName.includes('roster_file') && typeof index === 'number') {
    const file = target.files[0];
    manualOrderData.factory_products = [...manualOrderData.factory_products];
    const factoryProduct = manualOrderData.factory_products?.[index];
    const selectedProduct = factoryProduct.product;
    if (!factoryProduct) return false;

    factoryProduct.roster_file = file;

    // Remove roster file error if exists
    delete errorMessages[`factory_products[${index}].roster_file`];

    // Set a placeholder value for validation
    setFieldValue(`factory_products.${index}.roster_file`, 'file-uploaded');

    const ext = file.name.split('.').pop();
    if (ext !== 'xlsx') {
      showTimerAlert(
        'Invalid File Format',
        'The Excel file that was uploaded cannot be read, or it does not adhere to the template format. Please download the Excel template located next to the upload field, input your data there, and then attempt the upload again.',
        'error'
      );
      resetRosterData(index);
      target.value = null;
      return false;
    }

    try {
      const rows = await readXlsxFile(file);
      const isValidFormat = rows[0][0] === 'PRODUCTS';
      rows[0][1] === 'NAME ON PRODUCT' &&
        rows[0][2] === 'NUMBER' &&
        rows[0][3] === 'SIZE*' &&
        rows[0][4] === 'QUANTITY*';

      const isValidOptionalColumns =
        rows[0][5] === 'DESIGN NAME (Optional)' && rows[0][6] === 'DESIGN ID (Optional)';
      if (!isValidFormat) {
        showTimerAlert(
          'Invalid File Format',
          'The Excel file that was uploaded cannot be read, or it does not adhere to the template format. Please download the Excel template located next to the upload field, input your data there, and then attempt the upload again.',
          'error'
        );
        resetRosterData(index);
        target.value = null;
        return false;
      }
      if (isValidFormat && !isValidOptionalColumns) {
        const rosterDataArr: RosterData[] = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (row.length === 5 || row.length > 5) {
            const [product, name, number, size, quantity] = row;
            if (product !== 'Custom Product' && (size === null || quantity === null)) {
              continue;
            }
            let matchedFactoryProduct = getProductByName(product);
            if (!matchedFactoryProduct) {
              continue;
            }

            let matchedManualOrderFactoryProductIndex = getManualOrderFactoryProductIndex(
              manualOrderData.factory_products,
              matchedFactoryProduct
            );
            let derivedSizeIndex = -1;
            if (matchedFactoryProduct) {
              matchedFactoryProduct.sizes[0].json_data.forEach((sizeItem, sizeIndex) => {
                if (sizeItem.name === row[3]) {
                  derivedSizeIndex = sizeIndex;
                }
              });
            }
            let productIndex = null;
            if (matchedManualOrderFactoryProductIndex !== -1) {
              productIndex = matchedManualOrderFactoryProductIndex;
              // found index now push the roster data to the factoryProduct directly
              manualOrderData.factory_products[
                matchedManualOrderFactoryProductIndex
              ].roster_data.push({
                text: name ? name.toString() : '',
                number: number ? number.toString() : '',
                size: size ? size.toString() : '',
                quantity: parseInt(quantity.toString()),
                size_index: derivedSizeIndex,
                code: size ? size.toString() : '',
                information: '',
              });
            } else {
              // create factoryProduct
              let currentFactoryProduct = {
                ...factoryProduct,
              };
              let newFactoryProduct = {
                ...currentFactoryProduct,
                product: matchedFactoryProduct,
                product_name: name,
                roster_data: [], // Include playerNumber, size, quantity
                _uuid: uuidv4(),
              };
              newFactoryProduct.front_image = null;
              newFactoryProduct.back_image = null;
              newFactoryProduct.assets = [];
              newFactoryProduct.style = null;
              newFactoryProduct.enable_manual_roster = false;
              newFactoryProduct.roster_file = manualOrderData.is_common_rosters
                ? currentFactoryProduct.roster_file
                : null;

              newFactoryProduct.assets = [] as File[];

              newFactoryProduct.assets_info = [] as AssetInfo[];

              newFactoryProduct.roster_data.push({
                text: name ? name.toString() : '',
                number: number ? number.toString() : '',
                size: size ? size.toString() : '',
                quantity: parseInt(quantity.toString()),
                size_index: derivedSizeIndex,
                code: size ? size.toString() : '',
                information: '',
              });

              manualOrderData.factory_products.push(newFactoryProduct);
              activeAccordionIndex.value[index] = true;
              productIndex = manualOrderData.factory_products.length - 1;
            }

            manualOrderData.factory_products.forEach((_, productIndex) => {
              setCommonRosterData();
              setProductPriceObject(productIndex);
            });
          }
        }

        // If common rosters is checked, propagate to all products
        if (manualOrderData.is_common_rosters && index === 0) {
          manualOrderData.factory_products.forEach((product, idx) => {
            if (idx > 0 && product) {
              product.roster_file = file;
              product.roster_data = JSON.parse(JSON.stringify(rosterDataArr));
              setFieldValue(`factory_products.${idx}.roster_file`, 'file-uploaded');
              setFieldValue(`factory_products.${idx}.roster_data`, product.roster_data);
            }
          });
        }

        setProductPriceObject(index);
        showToast('Roster file processed successfully', 'success');
        if (typeof index === 'number') {
          navigateToProduct(index);
        }
      } else if (isValidFormat && isValidOptionalColumns) {
        let rosterData: RosterData;
        let productMap = new Map();
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (row.length === 5 || row.length > 5) {
            const [product, name, number, size, quantity, designName, designId] = row;
            let designNameVar = designName;
            let designIdVar = designId;
            let productName = product ? product.toString() : '';

            if (product !== 'Custom Product' && (size === null || quantity === null)) {
              continue;
            }
            if (designName === null) {
              designNameVar = '';
            }

            if (designId === null) {
              designIdVar = '';
            }

            let matchedFactoryProduct = getProductByName(product);

            let key = `${designNameVar}|${designIdVar}|${productName}`;

            // If key doesn't exist, initialize it
            if (!productMap.has(key)) {
              productMap.set(key, {
                name,
                designNameVar,
                designIdVar,
                productName,
                details: [], // Store playerNumber, size, quantity inside an array
              });
            }

            let derivedSizeIndex = -1;
            if (matchedFactoryProduct) {
              matchedFactoryProduct.sizes[0].json_data.forEach((sizeItem, sizeIndex) => {
                if (sizeItem.name === row[3]) {
                  derivedSizeIndex = sizeIndex;
                }
              });
            }
            rosterData = {
              text: name ? name.toString() : '',
              number: number ? number.toString() : '',
              size: size ? size.toString() : '',
              quantity: parseInt(quantity.toString()),
              size_index: derivedSizeIndex,
              code: size ? size.toString() : '',
              information: '',
            };
            productMap.get(key).details.push(rosterData);
          }
        }

        let loop_index = 0;
        let replacedFirst = false;

        // --- Pass 1: Handle Normal Products ---
        productMap.forEach(({ productName, name, designNameVar, designIdVar, details }) => {
          const isCustom = productName === 'Custom Product';
          if (isCustom) return; // skip custom here

          const matchedFactoryProduct = getProductByName(productName);
          const matchedManualOrderFactoryProductIndex = getManualOrderFactoryProductIndex(
            manualOrderData.factory_products,
            matchedFactoryProduct,
            designIdVar,
            designNameVar
          );

          let baseFactoryProduct = {
            ...factoryProduct,
            product_name: name,
            design_nick_name: designNameVar,
            design_id: designIdVar,
            roster_data: details,
            roster_file: loop_index === 0 ? factoryProduct.roster_file || file : null,
            _uuid: uuidv4(),
          };

          // Case 1: Replace uploaded slot if first product not matched
          if (loop_index === 0) {
            if (matchedManualOrderFactoryProductIndex === -1) {
              manualOrderData.factory_products[index] = {
                ...baseFactoryProduct,
                product: matchedFactoryProduct || null,
                is_new_product: false,
              };
              replacedFirst = true;
            }
          }

          // Case 2: Update or add new normal product
          if (matchedFactoryProduct) {
            if (matchedManualOrderFactoryProductIndex !== -1) {
              const existing =
                manualOrderData.factory_products[matchedManualOrderFactoryProductIndex];
              manualOrderData.factory_products[matchedManualOrderFactoryProductIndex] = {
                ...existing,
                roster_data: details,
                roster_file: existing.roster_file || (loop_index === 0 ? file : null),
              };
            } else if (!(loop_index === 0 && replacedFirst)) {
              baseFactoryProduct.product = matchedFactoryProduct;
              baseFactoryProduct.is_new_product = false;
              manualOrderData.factory_products.push(baseFactoryProduct);
            }
          }

          loop_index++;
        });

        // --- Pass 2: Always append Custom Products ---
        productMap.forEach(({ productName, name, designNameVar, designIdVar, details }) => {
          if (productName !== 'Custom Product') return;

          let newCustomProduct = {
            ...factoryProduct,
            product: null,
            is_new_product: true,
            new_product_name: productName,
            product_name: name,
            design_nick_name: designNameVar,
            design_id: designIdVar,
            roster_data: details,
            roster_file: loop_index === 0 && file ? file : null,
            front_image: null,
            back_image: null,
            assets: [],
            assets_info: [],
            style: null,
            enable_manual_roster: false,
          };

          manualOrderData.factory_products.push(newCustomProduct);
        });

        // --- Refresh pricing and common roster ---
        manualOrderData.factory_products.forEach((_, idx) => {
          setCommonRosterData();
          setProductPriceObject(idx);
        });

        showToast('Roster file processed successfully', 'success');
        if (typeof index === 'number') {
          navigateToProduct(index);
        }
      }
      target.value = null;
    } catch (error: any) {
      handleResponseException(error);
      showTimerAlert('Error', 'Failed to read Excel file', 'error');
      resetRosterData(index);
    }

    return true;
  }

  // Handle front/back images
  if (
    (fieldName.includes('front_image') || fieldName.includes('back_image')) &&
    typeof index === 'number'
  ) {
    const file = target.files[0];
    const factoryProduct = manualOrderData.factory_products?.[index];
    if (!factoryProduct) return false;

    const imageType = fieldName.includes('front_image') ? 'front_image' : 'back_image';

    factoryProduct[imageType] = file;

    showToast(
      `${imageType === 'front_image' ? 'Front' : 'Back'} image uploaded successfully`,
      'success'
    );
    if (typeof index === 'number') {
      navigateToProduct(index);
    }
    return true;
  } else {
    return false;
  }
};
function handlePaste(event: ClipboardEvent) {
  if (!focusedField.value) {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length === 0) return;
    pastedFiles.value = files;
    showQuickChooser.value = true;
    return;
  }
  const items = event.clipboardData?.items;
  if (!items) return;

  const files: File[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }

  if (files.length === 0) return;

  // Create synthetic event
  const syntheticEvent = {
    target: {
      files,
      value: null,
    },
  } as unknown as Event;

  handleFileUpload(
    syntheticEvent,
    focusedField.value.fieldName,
    focusedField.value.index,
    focusedField.value.accept
  );
}


function getProductByName(searchName, designId = null, designName = null) {
  return products.value.find((product) => {
    return product?.sku_id.toLowerCase() === searchName.toLowerCase();
  });
}

function getManualOrderFactoryProductIndex(
  factoryProducts,
  searchProduct,
  designId = null,
  designName = null
) {
  if (!searchProduct || !searchProduct?.sku || !searchProduct?.sku.sku_id) {
    return -1; // invalid input
  }

  const targetSkuId = searchProduct.sku.sku_id;
  const useBothDesignFields = designId !== null && designName !== null;
  const useDesignIdFieldOnly = designName !== null;
  const useDesignNameFieldOnly = designId !== null;

  return factoryProducts.findIndex((factoryProduct) => {
    const skuMatch = factoryProduct.product?.sku?.sku_id === targetSkuId;
    if (useBothDesignFields) {
      const designIdMatch = factoryProduct.design_id === designId;
      const designNameMatch = factoryProduct.design_nick_name === designName;
      return skuMatch && designIdMatch && designNameMatch;
    } else if (useDesignIdFieldOnly) {
      const designIdMatch = factoryProduct.design_id === designId;
      return skuMatch && designIdMatch;
    } else if (useDesignNameFieldOnly) {
      const designNameMatch = factoryProduct.design_nick_name === designName;
      return skuMatch && designNameMatch;
    } else {
      return skuMatch; // Only SKU match
    }
  });
}

// function getManualOrderFactoryProductIndex(factoryProducts, searchProduct, designId = null, designName = null) {
//   if (!searchProduct || !searchProduct.sku || !searchProduct.sku.sku_id) {
//     return -1; // invalid input
//   }
//   const targetSkuId = searchProduct.sku.sku_id;

//   let exactMatchIndex = -1;
//   let designIdMatchIndex = -1;
//   let designNameMatchIndex = -1;
//   let skuOnlyMatchIndex = -1;

//   factoryProducts.forEach((factoryProduct, index) => {
//     const skuMatch = factoryProduct.product?.sku?.sku_id === targetSkuId;
//     if (!skuMatch) return;

//     const hasDesignId = designId !== null;
//     const hasDesignName = designName !== null;

//     const designIdMatch = factoryProduct.design_id === designId;
//     const designNameMatch = factoryProduct.design_nick_name === designName;

//     if (hasDesignId && hasDesignName && designIdMatch && designNameMatch && exactMatchIndex === -1) {
//       exactMatchIndex = index;
//     } else if (hasDesignId && designIdMatch && designIdMatchIndex === -1) {
//       designIdMatchIndex = index;
//     } else if (hasDesignName && designNameMatch && designNameMatchIndex === -1) {
//       designNameMatchIndex = index;
//     } else if (skuOnlyMatchIndex === -1) {
//       skuOnlyMatchIndex = index;
//     }
//   });
//   // Return the best match available based on priority
//   if (exactMatchIndex !== -1) return exactMatchIndex;
//   if (designIdMatchIndex !== -1) return designIdMatchIndex;
//   if (designNameMatchIndex !== -1) return designNameMatchIndex;
//   return skuOnlyMatchIndex;
// }

function resetRosterData(factoryProductIndex: number) {
  const factoryProduct = manualOrderData.factory_products[factoryProductIndex];
  if (!factoryProduct) return;

  factoryProduct.roster_file = null;
  factoryProduct.roster_data = [];
  setProductPriceObject(factoryProductIndex);
}

function removeFile(factoryProductIndex: number, fileType: string) {
  const factoryProduct = manualOrderData.factory_products[factoryProductIndex];
  if (!factoryProduct) return;

  factoryProduct[fileType] = null;
}

function getFilePreview(file: File | string): string {
  if (!file) return '';
  return file instanceof File ? URL.createObjectURL(file) : `${storageUrl}/${file}`;
}

function isImageFile(extension: string): boolean {
  const imageExtensions = ['png', 'jpg', 'jpeg', 'svg', 'tif', 'tiff'];
  return imageExtensions.includes(extension);
}

function removeFactoryProductAsset(factoryProductIndex: number, assetIndex: number) {
  const factoryProduct = manualOrderData.factory_products[factoryProductIndex];
  if (!factoryProduct) return;

  if (assetIndex === -1) {
    factoryProduct.assets = [];
    factoryProduct.assets_info = [];
    setFieldValue(`factory_products.${factoryProductIndex}.assets`, []);
  } else {
    if (!factoryProduct.assets || !factoryProduct.assets_info) return;

    factoryProduct.assets.splice(assetIndex, 1);
    factoryProduct.assets_info.splice(assetIndex, 1);
    setFieldValue(
      `factory_products.${factoryProductIndex}.assets`,
      factoryProduct.assets.length > 0 ? 'files-uploaded' : null
    );
  }

  // If common assets is checked and this is the first product, update all products
  if (manualOrderData.is_common_assets && factoryProductIndex === 0) {
    manualOrderData.factory_products.forEach((product, idx) => {
      if (idx > 0 && product) {
        product.assets = [...(factoryProduct.assets || [])];
        product.assets_info = [...(factoryProduct.assets_info || [])];
        setFieldValue(
          `factory_products.${idx}.assets`,
          product.assets.length > 0 ? 'files-uploaded' : null
        );
      }
    });
  }
}

// FIXED: Added null checks to prevent errors
function handleProductCommonDataUpdate(event: Event, type: 'assets' | 'roster') {
  const isCommon = (event.target as HTMLInputElement).checked;

  if (type === 'assets') {
    manualOrderData.is_common_assets = isCommon;
  } else if (type === 'roster') {
    manualOrderData.is_common_rosters = isCommon;
  }

  if (isCommon) {
    // Get the first product's data
    const firstProduct = manualOrderData.factory_products[0];
    if (!firstProduct) return;

    manualOrderData.factory_products.forEach((factoryProduct, index) => {
      if (index > 0 && factoryProduct) {
        if (type === 'assets') {
          // Copy assets from first product
          factoryProduct.assets = firstProduct.assets ? [...firstProduct.assets] : [];
          factoryProduct.assets_info = firstProduct.assets_info
            ? [...firstProduct.assets_info]
            : [];
          setFieldValue(
            `factory_products.${index}.assets`,
            factoryProduct.assets.length > 0 ? 'files-uploaded' : null
          );
        } else if (type === 'roster') {
          // Copy roster from first product
          factoryProduct.roster_file = firstProduct.roster_file;
          factoryProduct.roster_data = firstProduct.roster_data
            ? JSON.parse(JSON.stringify(firstProduct.roster_data))
            : [];

          setFieldValue(
            `factory_products.${index}.roster_file`,
            factoryProduct.roster_file ? 'file-uploaded' : null
          );
          setFieldValue(`factory_products.${index}.roster_data`, factoryProduct.roster_data);

          // Update price object based on new roster data
          setProductPriceObject(index);
        }
      }
    });
  } else if (type === 'roster') {
    // If unchecking common rosters, reset all products except the first one
    manualOrderData.factory_products.forEach((factoryProduct, index) => {
      if (index > 0 && factoryProduct) {
        resetRosterData(index);
      }
    });
  } else if (type === 'assets') {
    // If unchecking common assets, clear assets for all products except the first one
    manualOrderData.factory_products.forEach((factoryProduct, index) => {
      if (index > 0 && factoryProduct) {
        factoryProduct.assets = [];
        factoryProduct.assets_info = [];
        setFieldValue(`factory_products.${index}.assets`, null);
      }
    });
  }
}

// FIXED: Added null checks to prevent errors
function setProductPriceObject(factoryProductIndex: number) {
  const factoryProduct = manualOrderData.factory_products[factoryProductIndex];
  if (!factoryProduct) return;

  factoryProduct.product_price_object = getProductPriceDefaultObject();

  // Calculate quantity from roster data
  if (factoryProduct.roster_data && factoryProduct.roster_data.length > 0) {
    let totalQuantity = 0;
    factoryProduct.roster_data.forEach((item) => {
      totalQuantity += item.quantity || 0;
    });
    factoryProduct.product_price_object.quantity = totalQuantity;
  }

  orderTotalPrice.value = getOrderTotalPrice();
}

function getNetPrice(netPrice: NetPrice): number {
  if (!netPrice || !netPrice.net_price) return 0;
  return parseFloat(netPrice.net_price.toString());
}

function hasGeneralInfoErrors(formErrors: any): boolean {
  return !!(
    formErrors.merchant ||
    formErrors.customer ||
    formErrors.customer_reference_no ||
    formErrors.shipping_address ||
    formErrors.custom_shipping_address
  );
}

function hasProductErrors(formErrors: any, index: number): boolean {
  const keysToCheck = ['product', 'style', 'assets', 'roster_file'];
  // Check flat string-based keys (e.g., "factory_products[2].style")
  const hasProductError = keysToCheck.some(
    (key) => formErrors?.[`factory_products[${index}].${key}`]
  );
  return hasProductError;
}

function handleAddonsSelection(selectedAddons: Addon[], factoryProductIndex: number) {
  setProductPriceObject(factoryProductIndex);
}

// Function to clear all error messages
const clearErrors = () => {
  Object.keys(errorMessages).forEach((key) => {
    errorMessages[key] = ''; // Clear error for each field
  });
};

async function createOrder() {
  if (is_custom_address.value) {
    manualOrderData.shipping_address = manualOrderData.custom_shipping_address;
    manualOrderData.is_custom_address = is_custom_address.value;
  }
  if (manualOrderData.shipping_address?.country_id) {
    unset(manualOrderData.shipping_address, 'country');
  }
  try {
    await schema.validate(manualOrderData, { abortEarly: false });
    clearErrors();
  } catch (err: any) {
    clearErrors();
    console.log('Validation errors:', err);
    err.inner.forEach((e: any) => {
      errorMessages[e.path] = e.message;
    });
    return;
  }
  const payload = getPayload(manualOrderData);

  try {
    loading.value = true;
    await http.post('create-manual-order', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    showTimerAlert('Success', 'Order created successfully', 'success');
    emit('onSave');
    closeManualOrderModal();
  } catch (error: any) {
    handleResponseException(error);
  } finally {
    loading.value = false;
  }
}

function closeManualOrderModal() {
  uploadSessionUUID.value = null;
  emit('close');
}

onMounted(async () => {
  await getManualOrderData();
  if (countries.value.length === 0) {
    await getCountries();
  }
});

const getCountries = async (): Promise<void> => {
  try {
    const response = await http.get('/addresses/countries');
    countries.value = response.data.result;
  } catch (error: any) {
    handleResponseException(error);
  }
};
const getValidAddons = (selected_addons) => {
  let validAddons = [];
  validAddons = selected_addons.map((addon: any): any => {
    if (addon && typeof addon === 'object' && 'title' in addon) {
      return addon; // Keep valid addons as-is
    } else if (typeof addon === 'string') {
      return { title: addon }; // Convert string to object with title key
    } else {
      return null;
    }
  }); // Remove null/invalid addons
  return validAddons;
};
const getShippingAddressInfoObject = (formData) => {
  const manual_order_data = formData;
  let address_id = null;
  let shipping_address = manual_order_data.custom_shipping_address;
  if (!is_custom_address.value && formData?.shipping_address) {
    address_id = formData?.shipping_address?.id;
    const {
      first_name,
      last_name,
      email,
      address1,
      address2,
      phone_number,
      city,
      zip_code,
      country,
      company_name,
      state,
    } = manual_order_data?.shipping_address;
    shipping_address = {
      first_name,
      last_name,
      email,
      address1,
      address2,
      phone_number,
      city,
      zip_code,
      country,
      company_name,
      state,
    };
  }
  return { address_id, shipping_address };
};
const getPayload = (formData) => {
  const manual_order_data = manualOrderData;

  const shipping_address_info_obj = getShippingAddressInfoObject(formData);
  let manual_order_payload = {
    company_id: formData.merchant.id,
    company_name: formData.merchant.company_name,
    customer_id: formData.customer.id,
    customer_name: formData.customer.name,
    design_file: '',
    po_number: formData.po_number,
    po_document: formData.po_document,
    order_type: JSON.stringify(manual_order_data.order_type),
    general_comments: formData.general_comments,
    customer_reference_no: formData.customer_reference_no,
    address_id: shipping_address_info_obj.address_id,
    save_address: manual_order_data.save_address,
    shipping_address: JSON.stringify(shipping_address_info_obj.shipping_address),
    factory_products: [],
    temp_uuid: uploadSessionUUID.value,
  };
  formData.factory_products.forEach((factory_product, factory_product_index) => {
    const {
      new_product_name,
      design_nick_name,
      design_id,
      product: selected_product,
      style: selected_style,
      roster_data,
    } = factory_product;

    let factory_id = null,
      factory_name = null,
      product_id = null,
      sku_number = '',
      product_name = '',
      product_type = '',
      measurement_ratio = 0,
      sizechart_reference = '',
      minimum_order_quantity = '',
      minimum_order_quantity_type = '',
      style_id = -1,
      style_name = '';
    if (!is_new_product.value[factory_product_index]) {
      factory_id = selected_product?.factory_id;
      factory_name = selected_product?.factory ? selected_product.factory.name : null;
      product_id = selected_product?.id;
      sku_number = selected_product?.sku.sku_number;
      product_name = selected_product?.sku.sku_id ?? '';
      product_type = selected_product?.product_type;
      sizechart_reference = selected_product?.sku.sizechart_reference;
      measurement_ratio = selected_product?.measurement_ratio;
      minimum_order_quantity = selected_product?.sku.minimum_order_quantity;
      minimum_order_quantity_type = selected_product?.sku.minimum_order_quantity_type;
    }
    if (selected_style) {
      style_name = selected_style?.name;
      style_id = selected_style?.id;
    }
    let addons = [];
    if (Array.isArray(factory_product.addons)) {
      addons = getValidAddons(factory_product.addons);
    }
    let assets: any = [];
    if (Array.isArray(factory_product.assets_info) && factory_product.assets_info.length > 0) {
      assets = factory_product.assets_info.map((info) => ({
        type: info.type || 'assets', // Default to "asset" if not present
        path: info.path,
        name: info.name,
        extension: info.extension,
        size: info.size,
      }));
    } else {
      assets = [];
    }
    const factory_product_data = {
      //id will be set on backend
      id: '',
      factory_id,
      factory_name,
      addons: JSON.stringify(addons),
      assets: assets,
      svg_url: '',
      sync_id: null,
      style_id,
      style_name,
      design_id: design_id,
      design_nick_name,
      svg_parts: JSON.stringify([]),
      back_image: factory_product.back_image || 'placeholders/custom-design.png',
      front_image: factory_product.front_image || 'placeholders/custom-design.png',
      is_new_product: !!factory_product.new_product_name || is_new_product.value[factory_product_index],
      new_product_name,
      product_id,
      sku_number,
      product_name,
      product_type,
      sort_order: factory_product_index + 1,
      reorder_data: null,
      grouped_addons: JSON.stringify([]),
      production_url: '',
      back_image_short: '',
      fixed_logo_index: 0,
      ungrouped_addons: JSON.stringify([]),
      front_image_short: '',
      is_custom_product: !!factory_product.new_product_name || is_new_product.value[factory_product_index],
      custom_product_placeholder: 'placeholders/custom-design.png',
      measurement_ratio,
      sizechart_reference,
      product_price_object: JSON.stringify(
        manual_order_data.is_common_rosters
          ? manual_order_data.factory_products[0].product_price_object
          : manual_order_data.factory_products[factory_product_index].product_price_object
      ),
      shuffle_color_number: 1,
      product_roster_detail: JSON.stringify(cleanRoster(roster_data)),
      size_variants_mapping: null,
      minimum_order_quantity,
      minimum_order_quantity_type,
      product_custom_text_objects: JSON.stringify({ common: [], roster: [] }),
      product_manual_price:  manual_order_data.factory_products[factory_product_index].product_price,
      // this is for manual order and product prices added manually. It will be removed after manual prices are used
      prices: {
        sizes: []
      },
       //this is check to see is it manual order and product prices added manually. It will be removed after manual prices are used
      removePricesObjectAfterUse: true
    };
    let selectedProduct = manual_order_data.factory_products[factory_product_index].product
    let productOriginalPrice = manual_order_data.factory_products[factory_product_index].product_price
    if(selectedProduct) {
      productOriginalPrice = manual_order_data.factory_products[factory_product_index].product.sku?.net_price?.net_price || '0'
    }
    let productFinalPrice = manual_order_data.factory_products[factory_product_index].product_price
    if(!productFinalPrice) {
      productFinalPrice = '0'
    }

    let productSizePrice: any = new Map();
    roster_data.forEach(rosterItem => {
      const existingRosterItem = productSizePrice.get(rosterItem.size)
      const sizeQuantity = rosterItem.quantity ? parseInt(rosterItem.quantity) : 0
      const priceWithQuantity = parseFloat(productFinalPrice) * sizeQuantity
      if(existingRosterItem) {
        productSizePrice.get(rosterItem.size).quantity += rosterItem.quantity
        productSizePrice.get(rosterItem.size).final_net_price_with_quantity += priceWithQuantity
      } else {
        const sizePriceInfo = {
          size: rosterItem.size, quantity: rosterItem.quantity, original_price: productOriginalPrice,
          final_net_price: manual_order_data.factory_products[factory_product_index].product_price,
          final_net_price_with_quantity: priceWithQuantity
        }
        productSizePrice.set(rosterItem.size, sizePriceInfo)
      }
    });
    productSizePrice = Array.from(productSizePrice.values())
    factory_product_data.prices.sizes = productSizePrice
    manual_order_payload.factory_products.push(factory_product_data);
  });
  if (formData) {
    return manual_order_payload;
  } else {
    const form_data_stringify_types = ['Object', 'Array', 'Number', 'Boolean'];
    let manual_order_form_data_payload = new FormData();
    for (const [manual_order_payload_key, manual_order_payload_value] of Object.entries(
      manual_order_payload
    )) {
      const need_to_stringify_value =
        manual_order_payload_value &&
        form_data_stringify_types.includes(manual_order_payload_value.constructor.name);
      if (need_to_stringify_value) {
        if (manual_order_payload_key === 'factory_products') {
          manual_order_payload_value.forEach((factory_product, factory_product_index) => {
            for (const [factory_product_key, factory_product_value] of Object.entries(
              factory_product
            )) {
              //keys that no need to append to payload
              if (factory_product_key !== 'assets_info') {
                if (factory_product_key === 'assets') {
                  if ((factory_product_value as any[])?.length > 0) {
                    (factory_product_value as any[])?.forEach((factory_product_asset) => {
                      manual_order_form_data_payload.append(
                        `factory_products[${factory_product_index}][assets][]`,
                        factory_product_asset
                      );
                    });
                  } else {
                    manual_order_form_data_payload.append(
                      `factory_products[${factory_product_index}][assets][]`,
                      JSON.stringify([])
                    );
                  }
                } else {
                  const stringify_factory_product_key =
                    factory_product_value &&
                    form_data_stringify_types.includes(factory_product_value.constructor.name);
                  const factory_product_key_value_processed = stringify_factory_product_key
                    ? JSON.stringify(factory_product_value)
                    : factory_product_value;
                  manual_order_form_data_payload.append(
                    `factory_products[${factory_product_index}][${factory_product_key}]`,
                    factory_product_key_value_processed as any
                  );
                }
              }
            }
          });
        } else {
          manual_order_form_data_payload.append(
            `${manual_order_payload_key}`,
            JSON.stringify(manual_order_payload_value)
          );
        }
      } else {
        manual_order_form_data_payload.append(
          `${manual_order_payload_key}`,
          manual_order_payload_value
        );
      }
    }
    return manual_order_form_data_payload;
  }
};
function cleanRoster(roster: any[]) {
  return roster.filter(
    (r) =>
      r.text?.toString().trim() ||
      r.number?.toString().trim() ||
      r.size?.toString().trim() ||
      (r.quantity && r.quantity !== 1) ||
      r.information?.toString().trim()
  );
}
function getFileExtension(file) {
  const name = file.name || '';
  return name.split('.').pop()?.toLowerCase() || '';
}

const quickChooserSupportedFields = computed<QuickChooserSupport>(() => {
  if (!pastedFiles.value || pastedFiles.value.length === 0) {
    return {
      assets: false,
      roster_file: false,
      front_image: false,
      back_image: false,
    };
  }

  const file = pastedFiles.value[0];
  const ext = getFileExtension(file);

  const support: QuickChooserSupport = {
    assets: false,
    roster_file: false,
    front_image: false,
    back_image: false,
  };

  if (["xlsx", "xls"].includes(ext)) {
    support.roster_file = true;
  }
  if (["png", "jpg", "jpeg", "svg", "tif", "tiff"].includes(ext)) {
    support.assets = true;
    support.front_image = true;
    support.back_image = true;
  }
  if (["pdf", "zip", "cdr"].includes(ext)) {
    support.assets = true;
  }

  return support;
});
watch(activeAccordionIndex, async (newVal) => {
  await nextTick();
  if (shouldScrollToAccordion.value) {
    Object.keys(newVal).forEach((key) => {
      if (newVal[key]) {
        const idx = Number(key);
        const el = productAccordionRefs.value[idx];
        if (el && 'scrollIntoView' in el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
    shouldScrollToAccordion.value = false;
  }
},{ deep: true } );
const handleAccordionToggle = (index: number) => {
  activeAccordionIndex.value[index] = !activeAccordionIndex.value[index];
};

const navigateToProduct = async (productIndex: number) => {
  // Get the product's UUID
  const product = manualOrderData.factory_products[productIndex];
  if (!product) return;
  
  // Close all accordions and open only the selected one
  activeAccordionIndex.value = {};
  activeAccordionIndex.value[productIndex] = true;
  
  // Store the UUID to scroll to
  shouldScrollToAccordion.value = true;
  scrollToProductUuid.value = product._uuid;
};

const handleCustomerCreated= (newCustomer: Customer) => {
      customers.value.push(newCustomer);
      customers.value = [...customers.value]; // trigger reactivity
        // select the same reference object stored in options
      manualOrderData.customer = customers.value[customers.value.length - 1];
      customerAddresses.value = newCustomer.addresses || [];
      manualOrderData.shipping_address = null;
      manualOrderData.custom_shipping_address = {
        id:0,
        first_name: newCustomer.first_name || '',
        last_name: newCustomer.last_name || '',
        email: newCustomer.email || '',
        address1: '',
        address2: '',
        phone_number: '',
        city: '',
        zip_code: '',
        country_id: null,
        country: '',
        company_name: newCustomer.company_name || '',
        state: '',
      };
      showCreateCustomer.value = false;
    }

    const handleCustomerCreateClose = () =>{
      showCreateCustomer.value = false;
      manualOrderData.customer = null;
    }
</script>

<style scoped lang="scss">
.input {
  @apply mt-1 block w-full rounded-[4px] outline-none px-2 py-1 border-[1px] border-[#3c3c3c42] focus:border-[#3c3c3c];
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s ease-out;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
}
</style>
